"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import type { StoreCategoryLeaf } from "@/src/features/categories/types";
import type {
  HomeFeaturedProduct,
  HomeGalleryProduct,
} from "@/src/features/products/types";

type HomeGalleryCategory = StoreCategoryLeaf & {
  children?: StoreCategoryLeaf[];
};

type HomeGalleryWorldProps = {
  items: HomeGalleryProduct[];
  products?: HomeFeaturedProduct[];
  categories?: HomeGalleryCategory[];
};

function TicketCard({
  category,
  large = false,
  priority = false,
}: {
  category: HomeGalleryCategory;
  large?: boolean;
  priority?: boolean;
}) {
  const imageSrc = category.file;

  if (!imageSrc) {
    return null;
  }

  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const [isMobileVisible, setIsMobileVisible] = useState(false);
  const visibleChildren = (category.children ?? []).slice(0, large ? 5 : 3);
  const imageSizes =
    "(max-width: 767px) calc(100vw - 24px), (max-width: 1023px) calc(50vw - 18px), (max-width: 1439px) 32vw, 477px";

  useEffect(() => {
    const cardElement = cardRef.current;

    if (!cardElement || typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    if (!mediaQuery.matches) {
      setIsMobileVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsMobileVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.35,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    observer.observe(cardElement);

    return () => observer.disconnect();
  }, []);

  return (
    <Link
      ref={cardRef}
      href={`/products?category=${category.id}&direct=1`}
      className={`group relative block overflow-hidden rounded-[1.35rem] ${large ? "h-[360px] md:h-full" : "h-[220px] md:h-full"
        }`}
    >
      <Image
        src={imageSrc}
        alt={category.name}
        fill
        quality={large ? 76 : 74}
        preload={priority}
        fetchPriority={priority ? "high" : undefined}
        loading={priority ? "eager" : "lazy"}
        sizes={imageSizes}
        className={`${large ? "md:object-cover" : "object-cover"} transition-transform duration-700 ease-out group-hover:scale-[1.04]`}
      />

      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/58 via-black/18 to-transparent" />

      {visibleChildren.length > 0 ? (
        <div className={`absolute bottom-5 left-3 z-10 flex flex-nowrap justify-start gap-1.5 overflow-hidden md:bottom-6 md:left-4 ${large ? "max-w-[calc(100%-6.5rem)]" : "max-w-[70%] md:max-w-[68%]"}`}>
          {visibleChildren.map((child) => (
            <span
              key={child.id}
              className="shrink-0 whitespace-nowrap rounded-full border border-white/35 bg-white/92 px-3 py-1 text-[0.72rem] font-extrabold text-[#242424] shadow-[0_10px_24px_rgba(0,0,0,0.18)] backdrop-blur-sm md:text-xs"
            >
              {child.name}
            </span>
          ))}
        </div>
      ) : null}

      <div className="absolute bottom-5 right-0 z-10 flex items-end md:bottom-6">
        <span
          className={`inline-flex items-center justify-center rounded-l-full rounded-r-[0.4rem] bg-[#ee376a] px-4 py-2.5 text-sm font-bold !text-white shadow-[0_16px_34px_rgba(238,55,106,0.34)] md:px-5 md:py-3 md:text-[1rem] ${isMobileVisible
              ? "animate-home-gallery-cta-in"
              : "translate-x-[calc(100%-12px)] opacity-0"
            } md:translate-x-[calc(100%-12px)] lg:translate-x-[calc(100%-16px)] md:opacity-100 md:group-hover:translate-x-0 md:transition-transform md:duration-500 md:ease-[cubic-bezier(0.25,1,0.5,1)]`}
        >
          تسوق الآن
        </span>
      </div>
    </Link>
  );
}

export default function HomeGalleryWorld({
  items: _items,
  products: _products = [],
  categories = [],
}: HomeGalleryWorldProps) {
  const showcaseCategories = categories
    .filter((category) => Boolean(category.file))
    .slice(0, 5);

  if (showcaseCategories.length < 5) {
    return null;
  }

  return (
    <section className="mx-auto w-[98%] py-6 sm:w-[97%] lg:w-[96%] lg:py-10">
      <div className="mb-5 text-center">
        <h2 className="text-[1.8rem] font-extrabold text-[#242424] md:text-[2.4rem]">
          بيتك له اختيار يليق بيه.. وYUMA تساعدك تلاقيه
        </h2>
        <p className="mt-2 text-sm font-semibold text-[#7a7a7a] md:text-[1rem]">
          اكتشفي مجموعاتنا من وحي أجمل جزر العالم
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-[0.75fr_0.75fr_1.15fr] md:grid-rows-[255px_255px] lg:grid-rows-[270px_270px] lg:gap-4">
        <TicketCard category={showcaseCategories[1]} />
        <TicketCard category={showcaseCategories[2]} />
        <TicketCard category={showcaseCategories[3]} />
        <TicketCard category={showcaseCategories[4]} />

        <div className="md:col-start-3 md:row-span-2 md:row-start-1 md:h-full">
          <TicketCard category={showcaseCategories[0]} large priority />
        </div>
      </div>
    </section>
  );
}